/**
 * `/review` — the unified confirmation queue (design §2.8).
 *
 * The keyboard model and the card live in `components/review-queue.tsx` and are
 * UNCHANGED: `items` was always a prop, so wiring the real procedure touched
 * this route and nothing else. That was the point of shipping the shell first.
 *
 * What this route adds is the decision plumbing, and one property is worth
 * stating because it is easy to get wrong in a keyboard-driven list: the queue
 * advances OPTIMISTICALLY (the component moves its cursor the moment a key is
 * pressed) while the mutation runs in the background. A user triaging at speed
 * must not wait a round trip per item. The cost is that a failed decision is
 * discovered a beat late, which is why a failure surfaces as a toast naming the
 * item rather than as a silent no-op.
 */

import { Badge, Button, toast } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
  type ReviewItem,
  useResolveReviewMutation,
  useReviewQueueQuery,
} from '../../api/queries.ts';
import { PageBody, PageHeader } from '../../components/page.tsx';
import {
  type ReviewDecision,
  ReviewQueue,
  type ReviewItem as ReviewQueueItem,
} from '../../components/review-queue.tsx';
import { RouteErrorPanel } from '../../components/route-error-boundary.tsx';
import { toClientError } from '../../lib/errors.ts';

export const Route = createFileRoute('/_authed/review')({
  component: ReviewPage,
  errorComponent: RouteErrorPanel,
});

/** Server-formatted strings pass straight through; the badge gets a short label. */
const KIND_LABELS: Readonly<Record<ReviewItem['kind'], string>> = {
  suggested_contact: 'new person',
  field_candidate: 'field',
  phone_link: 'phone match',
  // Mail sync raised it: an address that matched two contacts. Accepting means
  // "acknowledged"; nothing was merged and nothing was silently picked.
  duplicate_contact: 'possible duplicate',
};

function toQueueItem(item: ReviewItem): ReviewQueueItem {
  return {
    id: item.id,
    kind: KIND_LABELS[item.kind] ?? item.kind,
    // `title` and `detail` are formatted SERVER-side. Three clients each
    // inventing their own phrasing for the same row is how two of them end up
    // subtly wrong about what accepting does.
    title: item.title,
    detail: item.detail,
  };
}

function ReviewPage() {
  const query = useReviewQueueQuery();
  const resolve = useResolveReviewMutation();

  const items = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.items.map(toQueueItem)),
    [query.data],
  );
  const counts = query.data?.pages[0]?.counts ?? null;

  const onDecide = useCallback(
    (decision: ReviewDecision) => {
      resolve.mutate(
        {
          itemId: decision.id,
          action: decision.action === 'accepted' ? 'accept' : 'dismiss',
        },
        {
          onError: (error) => {
            /**
             * A toast, NOT an outbox entry.
             *
             * `reportMutationFailure` exists for writes that carry something the
             * user typed and would hate to lose. A review decision carries an
             * item id and a verb: the item is still in the queue, and re-pressing
             * one key is cheaper than a retry queue that has to reason about a
             * decision the server may or may not have applied.
             */
            const client = toClientError(error);
            toast.error(
              `Could not ${decision.action === 'accepted' ? 'accept' : 'dismiss'} that item`,
              { description: client.message },
            );
          },
        },
      );
    },
    [resolve],
  );

  return (
    <>
      <PageHeader
        title="Review"
        description="One place for everything the system guessed and a human confirms."
      />
      <PageBody>
        {counts === null ? null : (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary">{counts.total} waiting</Badge>
            <span>
              {counts.suggestedContacts} new people · {counts.fieldCandidates} proposed fields ·{' '}
              {counts.phoneLinks} phone matches
            </span>
          </div>
        )}

        {query.isError ? (
          <p className="text-sm text-destructive">{toClientError(query.error).message}</p>
        ) : null}

        <ReviewQueue items={items} onDecide={onDecide} />

        {query.hasNextPage ? (
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
      </PageBody>
    </>
  );
}
